import { createContext, useContext, useEffect, useState } from 'react';
import NotFound from '../common/NotFound';

const ParamsContext = createContext();
export const OutletContext = createContext();
const LocationContext = createContext();

export function useParams() {
    return useContext(ParamsContext);
}

export function useOutlet() {
    return useContext(OutletContext);
}

export function useLocation() {
    return useContext(LocationContext);
}

function getPathName(base) {
    let pathname = window.location.pathname;
    if (pathname.startsWith(base)) {
        pathname = pathname.replace(base, '');
    }

    return pathname;
}

let parsedRoutes = null;
export let createRoutes = (routes) => {
    function process(routes = [], exp = '', components = [], parsedRoutes = []) {
        for (let i = 0; i < routes.length; i++) {
            const route = routes[i];

            let path = route.path;
            const isEmpty = path?.length === 0;
            if (!isEmpty) {
                // Replace wildcard with capture group
                path = path.replace('*', '(.*)');
                // Replace integer parameter with named decimal capture group
                path = path.replace(/(:d_([a-zA-Z0-9]+))/g, '(?<d_$2>\\-?\\d+)')
                // Replace alphanumeric parameter with named capture group
                path = path.replace(/(:([a-zA-Z0-9]+))/g, '(?<$2>.*)');
            }
            path = exp.concat(path);

            const routeComponents = components.concat(route.component);
            const hasNestedRoutes = route.nested?.length > 0
            if (hasNestedRoutes) {
                parsedRoutes = process(route.nested, path, routeComponents, parsedRoutes);
            }

            if ((isEmpty && hasNestedRoutes) || (!isEmpty && hasNestedRoutes)) {
                continue;
            }

            parsedRoutes.push({
                exp: new RegExp(path),
                components: routeComponents
            });
        }
        return parsedRoutes;
    }

    // On the first call parse the routes into regexes
    parsedRoutes = process(routes);

    createRoutes = () => parsedRoutes;

    return createRoutes();
};

function routePath(routes, url) {
    let noMatch = true;
    let components = null, params = {};
    for (let i = 0; i < routes.length; i++) {
        const route = routes[i];
        if (!route.exp.test(url)) {
            continue;
        } else {
            noMatch = false;
        }

        const result = route.exp.exec(url);
        if (result != null) {
            const groups = result.groups;
            if (groups != null) {
                Object.keys(groups).forEach(k => {
                    if (k.startsWith('d_')) {
                        const name = k.replace('d_', '');
                        params[name] = parseInt(groups[k], 10);
                    } else {
                        params[k] = groups[k];
                    }
                });
            }
        }

        components = route.components;
        break;
    }

    if (noMatch) {
        components = [<NotFound />];
    }

    return [components, params];
}

export default function Router(props) {
    const routes = props.routes;
    const [data, setData] = useState(routePath(routes, getPathName(props.base)));

    useEffect(() => {
        function listener() {
            const result = routePath(routes, getPathName(props.base));
            setData(result);
        }

        window.addEventListener('popstate', listener);
        window.addEventListener('xs_navigation', listener);
        return () => {
            window.removeEventListener('popstate', listener);
            window.removeEventListener('xs_navigation', listener);
        };
    }, []);

    const [components, params] = [data[0], data[1]];

    return (
        <OutletContext.Provider value={components}>
            <LocationContext.Provider value={window.location.pathname}>
                <ParamsContext.Provider value={params}>
                    {props.children}
                </ParamsContext.Provider>
            </LocationContext.Provider>
        </OutletContext.Provider>
    );
}
